/**
 * @file Invoice.utils.ts
 * @description Utility functions for Invoice component
 * @module Invoices/Invoice
 */

import type { InvoiceListData } from "../InvoicesList.types";
import type { ExchangeRate } from "./Invoice.types";

/**
 * Generates fetch configuration for invoice data retrieval
 *
 * Determines which invoice ID to use based on:
 * 1. idFromRowOption (if provided)
 * 2. clickedIdIfMultiIds (matches against selectedRows)
 * 3. First selectedRow as fallback
 *
 * @param selectedRows - Array of selected invoice rows
 * @param idFromRowOption - Optional ID from row option context
 * @param clickedIdIfMultiIds - Optional ID when multiple IDs are selected
 * @returns Axios config object with invoice ID parameter
 *
 * @example
 * // Single selection
 * const config = fetchConfig([{ invoices__Id: 123 }]);
 * // Returns: { params: { Id: 123 } }
 *
 * @example
 * // Multiple selection with specific clicked ID
 * const config = fetchConfig(
 *   [{ invoices__Id: 123 }, { invoices__Id: 456 }],
 *   null,
 *   456
 * );
 * // Returns: { params: { Id: 456 } }
 */
export const fetchConfig = (
  selectedRows: InvoiceListData[],
  idFromRowOption?: React.ReactText | null,
  clickedIdIfMultiIds?: number | string
) => {
  const invoicesId = selectedRows.find(
    (r) =>
      String(r.invoices__Id) === String(clickedIdIfMultiIds) ||
      Number(r.invoices__Id) === Number(clickedIdIfMultiIds)
  )?.invoices__Id;
  return {
    params: {
      Id:
        idFromRowOption ??
        (clickedIdIfMultiIds
          ? (invoicesId ?? selectedRows[0]?.invoices__Id)
          : selectedRows[0]?.invoices__Id),
    },
  };
};

/**
 * Finds the exchange rate between two currencies
 *
 * Searches the exchange rates array for a matching currency pair
 *
 * @param exchangeRates - Array of available exchange rates
 * @param fromCurrencyID - Source currency ID
 * @param toCurrencyID - Target currency ID
 * @returns Exchange rate value or null if not found
 *
 * @example
 * const rates = [
 *   { fromCurrencyID: 1, toCurrencyID: 2, exchangeRate: 1.25 },
 *   { fromCurrencyID: 2, toCurrencyID: 3, exchangeRate: 0.85 }
 * ];
 *
 * const rate = findExchangeRate(rates, 1, 2);
 * // Returns: 1.25
 *
 * const notFound = findExchangeRate(rates, 1, 3);
 * // Returns: null
 */
export const findExchangeRate = (
  exchangeRates: ExchangeRate[],
  fromCurrencyID: number,
  toCurrencyID: number
): number | null => {
  if (!exchangeRates) return null;

  const rate = exchangeRates.find(
    (rate) => rate.fromCurrencyID === fromCurrencyID && rate.toCurrencyID === toCurrencyID
  );

  return rate ? rate.exchangeRate : null;
};

/**
 * Updates exchange rate and amount when currencies or values change
 *
 * Manages the automatic calculation of exchange amounts based on:
 * - Invoice currency changes
 * - Exchange currency changes
 * - Exchange rate changes
 * - Invoice amount changes
 *
 * Business Rules:
 * 1. When invoice/exchange currency changes → Update exchange rate from lookup table
 * 2. When exchange rate or invoice amount changes → Recalculate exchange amount
 * 3. Formula: exchangeAmount = invoiceAmount × exchangeRate
 *
 * @param values - Form values object (modified in place)
 * @param lastInvoiceCurrencyID - Previous invoice currency ID state
 * @param setLastInvoiceCurrencyID - State setter for invoice currency
 * @param lastExchangeCurrencyID - Previous exchange currency ID state
 * @param setLastExchangeCurrencyID - State setter for exchange currency
 * @param lastExchangeRate - Previous exchange rate state
 * @param setLastExchangeRate - State setter for exchange rate
 * @param lastInvoiceAmount - Previous invoice amount state
 * @param setLastInvoiceAmount - State setter for invoice amount
 *
 * @example
 * // Initial load - stores current values
 * updateExchangeRate(values, undefined, setter1, undefined, setter2, undefined, setter3, undefined, setter4);
 *
 * @example
 * // Currency change - auto-updates rate
 * // Before: { invoiceCurrencyID: 1, exchangeCurrencyID: 2 }
 * // After: { invoiceCurrencyID: 1, exchangeCurrencyID: 3, exchangeRate: 0.85 }
 *
 * @example
 * // Amount/rate change - auto-calculates exchange amount
 * // { invoiceAmount: 1000, exchangeRate: 1.25 }
 * // → exchangeAmount = 1250.00
 */
export const updateExchangeRate = (
  values: any,
  lastInvoiceCurrencyID: number | undefined,
  setLastInvoiceCurrencyID: (id: number) => void,
  lastExchangeCurrencyID: number | undefined,
  setLastExchangeCurrencyID: (id: number) => void,
  lastExchangeRate: string | number | undefined,
  setLastExchangeRate: (rate: string | number) => void,
  lastInvoiceAmount: string | number | undefined,
  setLastInvoiceAmount: (amount: string | number) => void
) => {
  // Set last exchange rate and amount on initial load
  if (values.exchangeRate && lastExchangeRate === undefined) {
    setLastExchangeRate(values.exchangeRate);
    return;
  }
  if (values.lastInvoiceAmount && lastInvoiceAmount === undefined) {
    setLastInvoiceAmount(values.invoiceAmount);
    return;
  }

  // Update exchange rate when invoice currency or exchange currency changes
  if (
    values.invoiceCurrencyID &&
    values.exchangeCurrencyID &&
    (values.invoiceCurrencyID !== lastInvoiceCurrencyID ||
      values.exchangeCurrencyID !== lastExchangeCurrencyID)
  ) {
    setLastInvoiceCurrencyID(values.invoiceCurrencyID);
    setLastExchangeCurrencyID(values.exchangeCurrencyID);

    const newExchangeRate = findExchangeRate(
      values.listExchangeRates,
      values.invoiceCurrencyID,
      values.exchangeCurrencyID
    );

    if (newExchangeRate !== null) {
      values.exchangeRate = newExchangeRate;
      setLastExchangeRate(newExchangeRate);
    }
  }

  // Update exchange amount when exchange rate or invoice amount changes
  const exchangeRateNum = parseFloat(values.exchangeRate);
  const invoiceAmountNum = parseFloat(values.invoiceAmount);
  if (
    (lastExchangeRate !== values.exchangeRate || lastInvoiceAmount !== values.invoiceAmount) &&
    !isNaN(exchangeRateNum) &&
    !isNaN(invoiceAmountNum) &&
    values.exchangeAmount !== invoiceAmountNum * exchangeRateNum
  ) {
    setLastExchangeRate(values.exchangeRate);
    setLastInvoiceAmount(values.invoiceAmount);

    values.exchangeAmount = parseFloat((invoiceAmountNum * exchangeRateNum).toFixed(2));
  }
};
